<template>
  <div class="hcl-form-element-skill-levels">
    <Multiselect
      :class="{
        'is-valid': validationStatus === 'valid',
        'is-invalid': validationStatus === 'invalid'
      }"
      class="hcl-form-element-skill-levels-input"
      ref="fieldRef"
      :name="fieldName"
      :inputId="fieldName"
      :disabled="isReadOnly(props.definitionField.field)"
      :required="props.definitionField.field.required"
      :hidden="props.definitionField.field.hidden"
      :options="
        async (query: string) => { return await performSkillSearch(query) }
      "
      v-model="selectValue"
      valueProp="value"
      label="label"
      placeholder=""
      :filterResults="false"
      :minChars="2"
      :resolveOnLoad="false"
      :delay="300"
      :searchable="true"
      @select="onSelectValueChange"
      :noOptionsText="fieldPlaceholderNoOptions"
      :noResultsText="fieldPlaceholderNoResults"
      :clearOnSearch="true"
      :clearOnSelect="true"
    />
    <div class="hcl-form-element-skill-levels-results">
      <div
        class="results-row"
        v-for="result of selectedSkillLevels"
        :key="result.id"
      >
        <div class="column-name">
          {{ result.text }}
        </div>
        <div class="column-selection-wrapper">
          <div class="column-selection">
            <template v-if="result.inputtype === '1'">
              <!-- hidden field is necessary for save as favorite handling -->
              <input
                type="hidden"
                :name="fieldName + '[' + result.id + ']'"
                :value="result.hcl_skill_level_selected"
              />
            </template>
            <template v-else>
              <Multiselect
                class="selection-select"
                :options="result.einstufung"
                label="kenntnis_einstufung"
                valueProp="id"
                v-model="result.hcl_skill_level_selected"
                :noOptionsText="fieldPlaceholderSkillLevelNoOptions"
              >
              </Multiselect>
              <!-- hidden field is necessary for save as favorite handling -->
              <input
                type="hidden"
                :name="fieldName + '[' + result.id + ']'"
                :value="result.hcl_skill_level_selected"
              />
            </template>
          </div>
          <div class="column-actions">
            <a
              class="btn"
              type="button"
              v-on:click.stop="onSelectedSkillLevelRemove(result.id)"
            >
              <i class="eii--fa6-regular--trash-can"></i>
            </a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, inject, onMounted } from 'vue'

import { useElement } from '@/components/FormElement/element'

import { useDataStore } from '@/stores/data'

import Multiselect from '@vueform/multiselect'

import { DefinitionFormField, DefinitionFormFieldField, DataFormField, DataFormLookup, EventTilesTileChangeHeight, EventFormInputCheckboxChanged } from '@/components/types'

interface Props {
  definitionField: DefinitionFormField
  dataField: DataFormField
  dataLookup: DataFormLookup | undefined
}

const props = withDefaults(defineProps<Props>(), {
  definitionField: undefined,
  dataField: undefined,
  dataLookup: undefined
})

const blockContentMetaId: string = inject('blockContentMetaId', '')

const { link } = useDataStore()

const bucket = link(blockContentMetaId)

const { fieldRef, isReadOnly, fieldName, validationStatus } = useElement(props)

onMounted(() => {
  if (props.definitionField.field.focus) fieldRef.value.$refs.multiselect.focus()
})

const emit = defineEmits(['hcl-tiles-tile-change-height'])

/*
 * General Handling
 */
const updateDataForField = () => {
  const match = bucket.value.fields.filter((field: DefinitionFormFieldField) => {
    return field.name === props.dataField.name
  })

  if (match.length === 0) return

  const value: any[] = []

  for (const option of selectedSkillLevels.value) {
    value.push(option)
  }

  match[0].value = value
}

const calculateTileHeight = (dataField: DataFormField) => {
  if (typeof dataField.value !== 'number' && dataField.value.length === 0) return 1

  let heightNew = 1
  if (dataField.value && Array.isArray(dataField.value)) {
    for (const skillLevel of dataField.value) {
      heightNew += 0.5 // 2 skill levels are equal in height to 1 row
    }
  }

  return Math.ceil(heightNew)
}

/*
 * Skill Levels Auto Complete Search Handling
 */
interface SelectValue {
  value: any
  label: string
}

const selectValue: Ref<SelectValue> = ref({ value: null, label: '' })

const selectOptions: Ref<any[]> = ref([])

const fieldPlaceholderNoOptions = computed(() => {
  return props.definitionField.field?.placeholder_no_options ?? 'Suche nach Kenntnisse (Min. 2 Buchstaben eingeben).'
})

const fieldPlaceholderNoResults = computed(() => {
  return props.definitionField.field?.placeholder_no_results ?? 'Es wurden keine Resultate für ihre Suche gefunden.'
})

const fieldQueryMinLength = computed(() => {
  return props.definitionField.field?.autosuggestminlength ?? 2
})

const clearSelectOptions = () => {
  selectOptions.value = []
}

const getOptions = async (query: string) => {
  return await performSkillSearch(query)
}

const performSkillSearch = async (query: string) => {

  clearSelectOptions()

  if (query.length <= fieldQueryMinLength.value) return

  const url = props.definitionField.field.autosuggesturl + query

  const response = await fetch(
    url
  )

  const data = await response.json()

  if ('skills' in data) {
    for (const entry of data.skills) {
      selectOptions.value.push({
        ...entry
      })
    }

    return data.skills.map((item: any): SelectValue => {
      return { value: item.id, label: item.text }
    })
  }

  return { value: null, label: '' }
}

const onSelectValueChange = (selectedSkillId: number) => {

  const emptySelection: SelectValue = { value: null, label: '' }

  const duplicate = selectedSkillLevels.value.filter((skill) => {
    return skill.id === selectedSkillId
  })

  if (duplicate.length > 0) {
    selectValue.value = emptySelection
    return
  }

  const match = selectOptions.value.filter((skill) => {
    return skill.id === selectedSkillId
  })

  if (match.length === 0) {
    selectValue.value = emptySelection
    return
  }

  const skill = match[0]

  let skillId: number | boolean | undefined = undefined

  if (skill.inputtype === '1') skillId = true

  if (skill.inputtype === '0') {
    const einstufung = match[0].einstufung
    skillId = einstufung[einstufung.length - 1].id
  }

  selectedSkillLevels.value.push({ ...match[0], hcl_skill_level_selected: skillId })

  selectValue.value = emptySelection

  fieldRef.value.clear()

  fieldRef.value.refreshOptions((response: any[]) => {
    if (response) response.splice(0, response.length)
  })

  updateDataForField()

  emit('hcl-tiles-tile-change-height', { tileHeight: calculateTileHeight(props.dataField) } as EventTilesTileChangeHeight)
}

/*
 * Skill Level Results Handling
 */
const selectedSkillLevels: Ref<any[]> = ref([])

const onSelectedSkillLevelCheckboxChanged = (event: EventFormInputCheckboxChanged) => {

  const match = selectedSkillLevels.value.filter((skill) => {
    return skill.id === event.id
  })

  if (match.length === 0) return

  match[0].hcl_skill_level_selected = event.checked

  updateDataForField()
}

const onSelectedSkillLevelRemove = (skillId: string) => {

  const index = selectedSkillLevels.value.findIndex((skill) => {
    return parseInt(skill.id) === parseInt(skillId)
  })

  if (index === -1) return

  selectedSkillLevels.value.splice(index, 1)

  updateDataForField()

  emit('hcl-tiles-tile-change-height', { tileHeight: calculateTileHeight(props.dataField) } as EventTilesTileChangeHeight)
}

const fieldPlaceholderSkillLevelNoOptions = computed(() => {
  return props.definitionField.field?.placeholder_skill_level_no_options ?? 'Keine Optionen gefunden.'
})

const initializeSelectionsSkillLevel = (dataField: DataFormField) => {
  if (typeof dataField.value !== 'number' && dataField.value.length === 0) return

  selectedSkillLevels.value = []

  if (dataField.value && Array.isArray(dataField.value)) {
    for (const skillLevel of dataField.value) {
      selectedSkillLevels.value.push(skillLevel)
    }
  }

  emit('hcl-tiles-tile-change-height', { tileHeight: calculateTileHeight(props.dataField) } as EventTilesTileChangeHeight)
}

initializeSelectionsSkillLevel(props.dataField)

watch(
  () => props.dataField,
  (n, o) => {
    initializeSelectionsSkillLevel(n)
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
.hcl-form-element-skill-levels {
  width: 100%;

  .hcl-form-element-skill-levels-results {
    display: flex;
    flex-direction: column;

    .results-row {
      padding-top: 0.25rem;
      padding-left: 0.25rem;
      display: flex;
      align-items: center;
      min-height: calc(1.5em + 0.75rem + 2px);
      justify-content: space-between;

      .column-name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        align-items: inherit;
      }

      .column-selection-wrapper {
        display: flex;
        width: fit-content;
        flex-wrap: nowrap;
        align-items: inherit;
        padding-left: 0.25rem;

        .column-selection {
          width: max-content;
          min-width: 15rem;

          .multiselect {
            min-height: calc(1.5em + 0.5rem);
            max-height: calc(1.5em + 0.5rem);
          }
        }
      }
    }
  }
}
</style>
